export const SECTION_TAGS = [
  "[intro]",
  "[verse]",
  "[pre-chorus]",
  "[chorus]",
  "[post-chorus]",
  "[bridge]",
  "[instrumental]",
  "[solo]",
  "[outro]",
] as const;

export const PERFORMANCE_CHIPS = [
  "breathy",
  "punchy",
  "whisper",
  "belt",
  "close",
  "airy",
  "spoken",
] as const;

export type SectionCue = { tag: string; cue: string };

export type Music3SectionTag =
  | "intro"
  | "verse"
  | "pre-chorus"
  | "chorus"
  | "post-chorus"
  | "bridge"
  | "instrumental"
  | "solo"
  | "outro";

const TAG_SET = new Set<string>([
  "intro",
  "verse",
  "pre-chorus",
  "chorus",
  "post-chorus",
  "bridge",
  "instrumental",
  "solo",
  "outro",
]);

const ALIAS: Record<string, Music3SectionTag> = {
  intro: "intro",
  verse: "verse",
  v: "verse",
  v1: "verse",
  v2: "verse",
  v3: "verse",
  chorus: "chorus",
  hook: "chorus",
  refrain: "chorus",
  "pre-chorus": "pre-chorus",
  prechorus: "pre-chorus",
  "pre chorus": "pre-chorus",
  "post-chorus": "post-chorus",
  postchorus: "post-chorus",
  "post chorus": "post-chorus",
  bridge: "bridge",
  "middle 8": "bridge",
  "middle eight": "bridge",
  instrumental: "instrumental",
  inst: "instrumental",
  interlude: "instrumental",
  break: "instrumental",
  solo: "solo",
  outro: "outro",
  ending: "outro",
  coda: "outro",
  fade: "outro",
};

const NUMBERED =
  /^(intro|verse|pre-chorus|pre chorus|prechorus|chorus|post-chorus|post chorus|postchorus|bridge|instrumental|solo|outro|hook)\s*(?:#|-)?\s*\d+$/i;

function foldKey(inner: string): string {
  return inner
    .trim()
    .toLowerCase()
    .replace(/[_/]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^(final|last|big|double)\s+/, "");
}

export function canonicalizeMusic3Tag(inner: string): Music3SectionTag | null {
  const key = foldKey(inner);
  if (!key) return null;
  if (TAG_SET.has(key)) return key as Music3SectionTag;
  if (ALIAS[key]) return ALIAS[key];
  const numbered = key.match(NUMBERED);
  if (numbered) {
    const base = foldKey(numbered[1]);
    if (TAG_SET.has(base)) return base as Music3SectionTag;
    if (ALIAS[base]) return ALIAS[base];
  }
  return null;
}

function stripBom(s: string): string {
  return s.replace(/^\uFEFF/, "");
}

export function splitLyricsCaption(raw: string): { lyrics: string; caption: string } {
  const text = stripBom(raw || "").replace(/\r\n/g, "\n");
  const lines = text.split("\n");
  const captionParts: string[] = [];
  const lyricLines: string[] = [];
  let inCaption = false;
  for (const line of lines) {
    const head = line.match(/^\s*(?:CAPTION|Caption|PROMPT|Prompt|INSTRUCTIONS|Instructions)\s*:\s*(.*)$/);
    if (head) {
      inCaption = true;
      if (head[1].trim()) captionParts.push(head[1].trim());
      continue;
    }
    if (inCaption) {
      captionParts.push(line);
      continue;
    }
    lyricLines.push(line);
  }
  return {
    lyrics: lyricLines.join("\n").trim(),
    caption: captionParts.join("\n").trim(),
  };
}

/** Official tags, in song order (repeats kept). `[verse 1]` → verse. */
export function parseSectionTagSequence(lyrics: string): Music3SectionTag[] {
  return normalizeMusic3Lyrics(lyrics).tags;
}

/** Unique `[verse]`-style tags for performance cue chips. */
export function parseSectionTags(lyrics: string): string[] {
  const found: string[] = [];
  for (const t of parseSectionTagSequence(lyrics)) {
    const bracket = `[${t}]`;
    if (!found.includes(bracket)) found.push(bracket);
  }
  return found;
}

export function normalizeMusic3Lyrics(raw: string): {
  lyrics: string;
  tags: Music3SectionTag[];
} {
  const { lyrics: body } = splitLyricsCaption(raw);
  const tags: Music3SectionTag[] = [];
  const out: string[] = [];
  for (const line of body.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (out.length && out[out.length - 1] !== "") out.push("");
      continue;
    }
    const m = trimmed.match(/^\[([^\]]+)\]\s*(.*)$/);
    if (m) {
      const canon = canonicalizeMusic3Tag(m[1]);
      const rest = m[2].trim();
      if (canon) {
        out.push(`[${canon}]`);
        tags.push(canon);
        if (rest) out.push(rest);
      } else if (rest) {
        out.push(rest);
      }
      continue;
    }
    out.push(trimmed);
  }
  let lyrics = out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
  if (lyrics && tags.length === 0) {
    lyrics = `[verse]\n${lyrics}`;
    tags.push("verse");
  }
  return { lyrics, tags };
}

/** MiniMax / sgl-omni: `input` must be at most this many characters. */
export const MUSIC3_INPUT_MAX = 4096;

const DEFAULT_CAPTION =
  "A clean contemporary pop song with a clear lead vocal, warm mix, 100 BPM";

/** Cut at a line break so Music 3 never 400s on `input`. */
export function clampMusic3Input(
  lyrics: string,
  max = MUSIC3_INPUT_MAX,
): { lyrics: string; truncated: boolean } {
  const src = (lyrics || "").trim();
  const units = [...src];
  if (units.length <= max) return { lyrics: src, truncated: false };
  let cut = units.slice(0, max).join("");
  const nl = cut.lastIndexOf("\n");
  if (nl >= Math.floor(max * 0.6)) cut = cut.slice(0, nl);
  cut = cut.replace(/\n\[[a-z-]+\]\s*$/i, "").trimEnd();
  if (!cut.trim()) cut = units.slice(0, max).join("").trimEnd();
  return { lyrics: cut, truncated: true };
}

export function prepareMusic3Caption(
  caption: string,
  tags: Music3SectionTag[],
  extraFromLyrics = "",
): string {
  let c = [caption, extraFromLyrics]
    .map((s) => (s || "").trim())
    .filter(Boolean)
    .join("\n\n")
    .replace(/[*_`#]+/g, "")
    .replace(/<\|([^>]+)\|>/g, (_, inner: string) => `${String(inner).replace(/[_-]+/g, " ").trim()} is noted`)
    .trim();
  if (!c) c = DEFAULT_CAPTION;
  if (tags.length && !/\bform\s*:/i.test(c)) {
    c = `Form: ${tags.join(" then ")}.\n\n${c}`;
  }
  return c;
}

export function prepareMusic3Prompt(
  lyricsRaw: string,
  captionRaw?: string,
): { lyrics: string; caption: string; tags: Music3SectionTag[]; truncated: boolean } {
  const split = splitLyricsCaption(lyricsRaw);
  const normalized = normalizeMusic3Lyrics(split.lyrics);
  const clamped = clampMusic3Input(normalized.lyrics);
  const tags = clamped.truncated
    ? normalizeMusic3Lyrics(clamped.lyrics).tags
    : normalized.tags;
  const caption = prepareMusic3Caption(captionRaw || "", tags, split.caption);
  return { lyrics: clamped.lyrics, caption, tags, truncated: clamped.truncated };
}

export function insertTag(lyrics: string, tag: string, cursor: number): string {
  const before = lyrics.slice(0, cursor);
  const after = lyrics.slice(cursor);
  const nlBefore = before.endsWith("\n") || before.length === 0 ? "" : "\n";
  const nlAfter = after.startsWith("\n") ? "" : "\n";
  return `${before}${nlBefore}${tag}${nlAfter}${after}`;
}
